import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import toast from 'react-hot-toast';

import {
  SpaceReadModel,
  useGetSpacesByBuildingIdAssociationMatrixQuery,
  usePutSpacesByIdAssociationsMutation,
} from '@client/api';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyPlaceholder,
  isDefinedString,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeadCell,
  TableHeadRow,
  TableRow,
  Text,
  Typography,
} from '@client/toolkit';

import { SpacesAddModal } from '../../../../../components';
import { buildAssociationMatrix } from './build-association-matrix';

const ALL = 'all';

export function BuildingAssociationMatrix() {
  const { t: tCommon } = useTranslation();
  const { t: tGlobal } = useTranslation('global');
  const { t: tType } = useTranslation('type');

  const { buildingId } = useParams<{ buildingId: string }>();

  const { data, isFetching, isLoading, refetch } = useGetSpacesByBuildingIdAssociationMatrixQuery(
    { buildingId: buildingId ?? '' },
    { skip: !buildingId },
  );
  const [putAssociations] = usePutSpacesByIdAssociationsMutation();

  const matrix = useMemo(
    () => buildAssociationMatrix(data?.spaces ?? [], data?.associations ?? []),
    [data],
  );
  const spacesById = useMemo(() => new Map((data?.spaces ?? []).map((s) => [s.id, s])), [data]);
  const updatable = useMemo(() => new Set(data?.updatableSpaceIds ?? []), [data]);

  const [typeFilter, setTypeFilter] = useState<string>(ALL);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const types = useMemo(() => {
    const seen: string[] = [];
    matrix.areas.forEach((a) => {
      if (!seen.includes(a.discriminator)) seen.push(a.discriminator);
    });
    return seen;
  }, [matrix]);

  const owners = matrix.areas.filter((o) => typeFilter === ALL || o.discriminator === typeFilter);

  const associatedAreasOf = (ownerId: string) => matrix.areas.filter((a) => matrix.isAssociated(ownerId, a.id));

  const handleSubmit = async (ownerId: string, items: SpaceReadModel[]) => {
    setSaving(true);
    try {
      await putAssociations({ id: ownerId, body: items.map((x) => x.id) }).unwrap();
      await refetch();
      setEditingId(null);
    } catch {
      toast.error(tGlobal('space.associationsSaveError'));
    } finally {
      setSaving(false);
    }
  };

  const filterButton = (key: string, label: string) => (
    <button
      key={key}
      type="button"
      onClick={() => setTypeFilter(key)}
      className={[
        'rounded-full px-4 py-1.5 text-sm font-medium transition-colors',
        typeFilter === key ? 'bg-brand-primary text-black' : 'bg-surface-elevated text-gray-300 hover:text-white',
      ].join(' ')}
    >
      {label}
    </button>
  );

  const editingSelected = editingId
    ? (associatedAreasOf(editingId)
        .map((a) => spacesById.get(a.id))
        .filter(Boolean) as SpaceReadModel[])
    : [];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex-1" variant="highlighted">
          {tGlobal('space.associations')}
        </CardTitle>
      </CardHeader>

      <CardContent variant="fill" className="rounded-lg w-full px-4 pb-4">
        {isLoading ? (
          <div className="space-y-3 py-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full rounded-md" />
            ))}
          </div>
        ) : !isFetching && matrix.areas.length === 0 ? (
          <EmptyPlaceholder>
            {tCommon('noEntitySpecified', { entity: tGlobal('space.associations').toLowerCase() })}
          </EmptyPlaceholder>
        ) : (
          <>
            {/* Type filter */}
            <div className="flex flex-wrap items-center gap-2 pb-4">
              <span className="text-sm text-gray-400">{tGlobal('space.filterByType')}</span>
              {filterButton(ALL, tCommon('all'))}
              {types.map((type) => filterButton(type, tType(`SpaceDiscriminator.${type}`)))}
            </div>

            {/* One section per owner space */}
            <div className="space-y-2">
              {owners.map((owner) => {
                const associated = associatedAreasOf(owner.id);
                const canEdit = updatable.has(owner.id);
                return (
                  <div
                    key={owner.id}
                    className="overflow-hidden rounded-lg border border-border bg-surface-elevated/40"
                  >
                    <div className="flex items-center justify-between gap-3 px-4 py-3">
                      <div>
                        <span className="text-base font-semibold text-white">{owner.name}</span>
                        <Typography variant="sub" className="mt-1 block">
                          {tType(`SpaceDiscriminator.${owner.discriminator}`)}
                        </Typography>
                      </div>
                      <Button size="sm" color="primary" disabled={!canEdit} onClick={() => setEditingId(owner.id)}>
                        {tCommon('edit')}
                      </Button>
                    </div>
                    <Table className="w-full" layout="fixed">
                      {associated.length === 0 && (
                        <caption className="px-4 py-4 [caption-side:bottom]">
                          <EmptyPlaceholder>
                            {tCommon('noEntitySpecified', {
                              entity: tGlobal('space.associations').toLowerCase(),
                            })}
                          </EmptyPlaceholder>
                        </caption>
                      )}
                      <TableHead>
                        <TableHeadRow>
                          <TableHeadCell className="w-[50%]">{tGlobal('space.name')}</TableHeadCell>
                          <TableHeadCell>{tGlobal('space.type')}</TableHeadCell>
                        </TableHeadRow>
                      </TableHead>
                      <TableBody>
                        {associated.map((area) => (
                          <TableRow key={area.id}>
                            <TableCell>
                              <Text className="font-medium" isEmpty={!isDefinedString(area.name)}>
                                {area.name}
                              </Text>
                            </TableCell>
                            <TableCell>
                              <Text isEmpty={!isDefinedString(area.discriminator)}>
                                {area.discriminator ? tType(`SpaceDiscriminator.${area.discriminator}`) : '-'}
                              </Text>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </CardContent>

      {editingId && (
        <SpacesAddModal
          open
          selected={editingSelected}
          rootId={buildingId ?? undefined}
          showHierarchy
          requiredPackage={null}
          excludeIds={[editingId, buildingId].filter(isDefinedString)}
          isSubmitting={saving}
          onClose={() => setEditingId(null)}
          onSubmit={(items) => handleSubmit(editingId, items)}
        />
      )}
    </Card>
  );
}
